import { formatBRL, shoppingListText } from '@deizi/shared';
import { Link } from 'react-router';
import { EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { formatG, useSetShopping, useShoppingList } from '../../lib/inventory-api';

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** "Lista de compras" tab (board Filamentos-Compras). */
export function ShoppingTab() {
  const list = useShoppingList();
  const toast = useToast();
  const setQty = useSetShopping();
  if (list.isPending) return <Skeleton rows={3} height={48} />;
  if (list.isError) {
    return <ErrorBox title="Não foi possível montar a lista" onRetry={() => void list.refetch()} />;
  }
  const edited = list.data;
  const filamentTotal = edited.filaments.reduce((n, l) => n + l.subtotalCentavos, 0);
  const spools = edited.filaments.reduce((n, l) => n + l.spools, 0);
  if (edited.filaments.length === 0 && edited.supplies.length === 0) {
    return (
      <section className="card">
        <EmptyState
          title="Nada na lista de compras"
          action={
            <Link className="btn" to="/filamentos">
              Ver catálogo
            </Link>
          }
        >
          Cadastre filamentos com estoque mínimo e os que faltarem aparecem aqui.
        </EmptyState>
      </section>
    );
  }
  const cols = '2fr 1.2fr 130px 1fr 1fr';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
      <section className="card flat" style={{ flex: '3 1 560px', minWidth: 0 }}>
        <div className="row between" style={{ padding: '16px 18px 6px' }}>
          <h2>Para comprar</h2>
          <span className="xs muted">preço da última compra, com frete</span>
        </div>
        <div className="gt" style={{ overflowX: 'auto' }}>
          <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 620 }}>
            <span>Filamento</span>
            <span className="num">Estoque / mínimo</span>
            <span className="num">Carretéis</span>
            <span className="num">Por carretel</span>
            <span className="num">Subtotal</span>
          </div>
          {edited.filaments.map((l) => (
            <div
              key={l.filamentId}
              className="gtr"
              style={{ gridTemplateColumns: cols, minWidth: 620 }}
            >
              <span style={{ minWidth: 0 }}>
                <span className="trunc" style={{ fontWeight: 500, display: 'block' }}>
                  {l.name}
                </span>
                <span className="xs muted">
                  {l.lastSupplier ?? l.material}
                  {l.adjusted ? (
                    <>
                      {' · ajustado à mão · '}
                      <button
                        type="button"
                        className="btn sm g"
                        style={{ padding: 0, minHeight: 0 }}
                        onClick={() =>
                          setQty.mutate({ kind: 'filaments', id: l.filamentId, quantity: null })
                        }
                      >
                        {l.suggested ? `voltar para ${l.suggested}` : 'tirar da lista'}
                      </button>
                    </>
                  ) : null}
                </span>
              </span>
              <span className="num">
                <span style={{ color: 'var(--warn)' }}>{formatG(l.stockGrams)}</span> /{' '}
                {formatG(l.minimumGrams)}
              </span>
              <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn sm icon"
                  aria-label={`Menos um carretel de ${l.name}`}
                  disabled={l.spools <= 0 || setQty.isPending}
                  onClick={() =>
                    setQty.mutate({
                      kind: 'filaments',
                      id: l.filamentId,
                      quantity: Math.max(0, l.spools - 1),
                    })
                  }
                >
                  −
                </button>
                <span className="tab" style={{ minWidth: 24, textAlign: 'center' }}>
                  {l.spools}
                </span>
                <button
                  type="button"
                  className="btn sm icon"
                  aria-label={`Mais um carretel de ${l.name}`}
                  disabled={setQty.isPending}
                  onClick={() =>
                    setQty.mutate({ kind: 'filaments', id: l.filamentId, quantity: l.spools + 1 })
                  }
                >
                  +
                </button>
              </span>
              <span className="num">{formatBRL(l.pricePerSpoolCentavos)}</span>
              <span className="num">{formatBRL(l.subtotalCentavos)}</span>
            </div>
          ))}
          {edited.filaments.length === 0 ? (
            <div className="gtr" style={{ gridTemplateColumns: '1fr' }}>
              <span className="sm muted">Nada abaixo do mínimo agora.</span>
            </div>
          ) : (
            <div className="gtr foot" style={{ gridTemplateColumns: cols, minWidth: 620 }}>
              <span>Total estimado</span>
              <span />
              <span className="num">{spools}</span>
              <span />
              <span className="num">{formatBRL(filamentTotal)}</span>
            </div>
          )}
        </div>
        {edited.supplies.length > 0 ? (
          <div style={{ padding: '12px 18px 16px' }}>
            <p className="lbl">Insumos</p>
            {edited.supplies.map((l) => (
              <div key={l.supplyId} className="kv sm">
                <span>
                  {l.name} · comprar {l.quantity.toLocaleString('pt-BR')} (tem{' '}
                  {l.quantityOnHand.toLocaleString('pt-BR')}, mínimo{' '}
                  {l.minimumQuantity.toLocaleString('pt-BR')})
                </span>
                <span className="v">{formatBRL(l.subtotalCentavos)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </section>
      <aside className="card col" style={{ flex: '1 1 260px', gap: 10 }}>
        <h2>Comprar</h2>
        <p className="sm muted">
          Leva {spools} {spools === 1 ? 'carretel' : 'carretéis'}. Frete e preço real você confere
          ao registrar a compra.
        </p>
        <p className="fr tab" style={{ fontSize: 26 }}>
          {formatBRL(filamentTotal + edited.supplies.reduce((n, l) => n + l.subtotalCentavos, 0))}
        </p>
        <button
          type="button"
          className="btn"
          onClick={() =>
            void copyText(shoppingListText(edited)).then((ok) =>
              toast(ok ? 'Copiado. É só colar.' : 'Não deu para copiar neste navegador.'),
            )
          }
        >
          Copiar lista
        </button>
        <Link className="btn p" to="/filamentos/compra">
          Registrar compra
        </Link>
      </aside>
    </div>
  );
}
